import type {
  AdminChatMembershipCountBucket,
  AdminChatMessageLengthBucket,
  AdminChatRoomKind,
  AdminCommunityContentLengthBucket,
  AdminCommunityImageCountBucket,
  AdminCommunityTopicTag,
  AdminCommunityUploadSourceBucket,
  AdminHealthAnalysisFallbackPath,
  AdminHealthConfirmToSaveLatencyBucket,
  AdminHealthFileSizeBucket,
  AdminHealthFileTypeBucket,
  AdminMedicationDosePresence,
  AdminMedicationOccurrenceActionKind,
  AdminMedicationScheduleRecurrenceBucket,
  AdminMedicationSnoozeWindowBucket,
  AdminPetAttemptCountBucket,
  AdminPetBreedSelectionBucket,
  AdminPetPhotoConfirmLatencyBucket,
  AdminPetProfileCompletionStep,
  AdminReceiptCategoryMutationType,
  AdminReceiptExtractSourceBucket,
  AdminReceiptImageByteSizeBucket,
  AdminReceiptOcrConfidenceBucket,
  AdminRideLongestAddressLengthBucket,
  AdminRideVisitLeadTimeBucket,
  AdminPetSpeciesBucket,
  AdminSocialProfileMutationFieldSetBucket,
  AdminSocialPublicPetCountBucket,
  AdminSocialTargetUserLookupKind,
  AdminTelemetryDraftSourceBucket,
  AdminTelemetryDeviceModelBucket,
  AdminTelemetryDeviceModelNameBucket,
  AdminTelemetryLocaleBucket,
  AdminTelemetryMediaAttachCountBucket,
  AdminTelemetryNetworkTypeBucket,
  AdminTelemetryOsMajorBucket,
  AdminTelemetryOsVersionMinorBucket,
  AdminTelemetryPhysicalDeviceBucket,
  AdminTelemetryReauthMethodBucket,
  AdminTelemetryRecentAuthAgeBucket,
  AdminTelemetryTimezoneNameBucket,
  AdminTelemetryTimezoneOffsetBucket,
} from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "../locale/admin-dashboard-locale";

function text(locale: AdminDashboardLocale, en: string, ko: string): string {
  return locale === "ko" ? ko : en;
}

function compactLabel(value: string): string | null {
  const normalized = value.trim();
  if (!normalized || normalized === "-") {
    return null;
  }
  return normalized;
}

export function formatAdminTelemetryLocaleLabel(
  value: AdminTelemetryLocaleBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "ko":
      return text(locale, "Korean", "한국어");
    case "en":
      return text(locale, "English", "영어");
    case "other":
      return text(locale, "Other locale", "기타 언어");
    case "unknown":
      return text(locale, "Unknown locale", "알 수 없는 언어");
    default:
      return "-";
  }
}

export function formatAdminTelemetryAppBuildNumberLabel(
  value: string | undefined,
  locale: AdminDashboardLocale,
): string {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return text(locale, "Unknown build", "알 수 없는 빌드");
}

export function formatAdminTelemetryTimezoneOffsetBucketLabel(
  value: AdminTelemetryTimezoneOffsetBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "utc_minus_12_to_5":
      return text(locale, "UTC -12 to -5", "UTC -12~-5");
    case "utc_minus_4_to_plus_3":
      return text(locale, "UTC -4 to +3", "UTC -4~+3");
    case "utc_plus_4_to_plus_9":
      return text(locale, "UTC +4 to +9", "UTC +4~+9");
    case "utc_plus_10_to_plus_14":
      return text(locale, "UTC +10 to +14", "UTC +10~+14");
    case "unknown":
      return text(locale, "Unknown timezone", "알 수 없는 시간대");
    default:
      return "-";
  }
}

export function formatAdminTelemetryTimezoneNameBucketLabel(
  value: AdminTelemetryTimezoneNameBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "asia_seoul":
      return text(locale, "Asia/Seoul region", "아시아/서울 권역");
    case "asia_tokyo":
      return text(locale, "Asia/Tokyo region", "아시아/도쿄 권역");
    case "asia_east":
      return text(locale, "East Asia region", "동아시아 권역");
    case "southeast_asia":
      return text(locale, "Southeast Asia region", "동남아시아 권역");
    case "south_asia":
      return text(locale, "South Asia region", "남아시아 권역");
    case "europe":
      return text(locale, "Europe region", "유럽 권역");
    case "north_america_pacific":
      return text(locale, "North America Pacific", "북미 태평양 권역");
    case "north_america_mountain":
      return text(locale, "North America Mountain", "북미 산악 권역");
    case "north_america_central":
      return text(locale, "North America Central", "북미 중부 권역");
    case "north_america_eastern":
      return text(locale, "North America Eastern", "북미 동부 권역");
    case "oceania":
      return text(locale, "Oceania region", "오세아니아 권역");
    case "south_america":
      return text(locale, "South America region", "남아메리카 권역");
    case "middle_east_africa":
      return text(locale, "Middle East or Africa", "중동 또는 아프리카 권역");
    case "other":
      return text(locale, "Other timezone region", "기타 시간대 권역");
    case "unknown":
      return text(locale, "Unknown timezone region", "알 수 없는 시간대 권역");
    default:
      return "-";
  }
}

export function formatAdminTelemetryDeviceModelBucketLabel(
  value: AdminTelemetryDeviceModelBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "apple_phone":
      return text(locale, "Apple phone", "애플 휴대폰");
    case "apple_tablet":
      return text(locale, "Apple tablet", "애플 태블릿");
    case "android_phone":
      return text(locale, "Android phone", "안드로이드 휴대폰");
    case "android_tablet":
      return text(locale, "Android tablet", "안드로이드 태블릿");
    case "web_desktop":
      return text(locale, "Web desktop", "웹 데스크톱");
    case "web_mobile":
      return text(locale, "Web mobile", "웹 모바일");
    case "unknown":
      return text(locale, "Unknown device class", "알 수 없는 기기 분류");
    default:
      return "-";
  }
}

export function formatAdminTelemetryDeviceModelNameBucketLabel(
  value: AdminTelemetryDeviceModelNameBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "apple_iphone":
      return text(locale, "iPhone family", "iPhone 계열");
    case "apple_ipad":
      return text(locale, "iPad family", "iPad 계열");
    case "samsung_galaxy":
      return text(locale, "Samsung Galaxy family", "삼성 Galaxy 계열");
    case "samsung_other":
      return text(locale, "Samsung device", "삼성 기기");
    case "google_pixel":
      return text(locale, "Google Pixel family", "Google Pixel 계열");
    case "android_oem_other":
      return text(locale, "Other Android OEM", "기타 Android 제조사");
    case "web_browser":
      return text(locale, "Web browser", "웹 브라우저");
    case "unknown":
      return text(locale, "Unknown device family", "알 수 없는 기기 계열");
    default:
      return "-";
  }
}

export function formatAdminTelemetryOsMajorBucketLabel(
  value: AdminTelemetryOsMajorBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "ios_17_or_lower":
      return text(locale, "iOS 17 or lower", "iOS 17 이하");
    case "ios_18_plus":
      return text(locale, "iOS 18+", "iOS 18+");
    case "android_13_or_lower":
      return text(locale, "Android 13 or lower", "Android 13 이하");
    case "android_14_plus":
      return text(locale, "Android 14+", "Android 14+");
    case "web_modern":
      return text(locale, "Modern web", "최신 웹");
    case "unknown":
      return text(locale, "Unknown OS family", "알 수 없는 OS 분류");
    default:
      return "-";
  }
}

export function formatAdminTelemetryOsVersionMinorBucketLabel(
  value: AdminTelemetryOsVersionMinorBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "ios_17_or_lower":
      return text(locale, "iOS 17 or lower", "iOS 17 이하");
    case "ios_18_0_x":
      return text(locale, "iOS 18.0.x", "iOS 18.0.x");
    case "ios_18_1_x":
      return text(locale, "iOS 18.1.x", "iOS 18.1.x");
    case "ios_18_2_plus":
      return text(locale, "iOS 18.2+", "iOS 18.2+");
    case "ios_19_plus":
      return text(locale, "iOS 19+", "iOS 19+");
    case "android_13_or_lower":
      return text(locale, "Android 13 or lower", "Android 13 이하");
    case "android_14_0_x":
      return text(locale, "Android 14.0.x", "Android 14.0.x");
    case "android_14_1_plus":
      return text(locale, "Android 14.1+", "Android 14.1+");
    case "android_15_plus":
      return text(locale, "Android 15+", "Android 15+");
    case "web_runtime":
      return text(locale, "Web runtime", "웹 런타임");
    case "unknown":
      return text(locale, "Unknown OS minor", "알 수 없는 OS 세부 버전");
    default:
      return "-";
  }
}

export function formatAdminTelemetryNetworkTypeBucketLabel(
  value: AdminTelemetryNetworkTypeBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "wifi":
      return text(locale, "Wi-Fi", "와이파이");
    case "cellular":
      return text(locale, "Cellular", "셀룰러");
    case "ethernet":
      return text(locale, "Ethernet", "이더넷");
    case "bluetooth":
      return text(locale, "Bluetooth", "블루투스");
    case "vpn":
      return text(locale, "VPN", "VPN");
    case "wimax":
      return text(locale, "WiMAX", "WiMAX");
    case "none":
      return text(locale, "Offline", "오프라인");
    case "other":
      return text(locale, "Other network", "기타 네트워크");
    case "unknown":
      return text(locale, "Unknown network", "알 수 없는 네트워크");
    default:
      return "-";
  }
}

export function formatAdminTelemetryPhysicalDeviceBucketLabel(
  value: AdminTelemetryPhysicalDeviceBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "physical":
      return text(locale, "Physical device", "실기기");
    case "simulator_or_emulator":
      return text(locale, "Simulator or emulator", "시뮬레이터 또는 에뮬레이터");
    case "unknown":
      return text(locale, "Unknown device runtime", "알 수 없는 기기 런타임");
    default:
      return "-";
  }
}

export function formatAdminTelemetryRuntimeSummaryLabel(
  input: {
    appBuildNumber?: string;
    deviceModelNameBucket?: AdminTelemetryDeviceModelNameBucket;
    osVersionMinorBucket?: AdminTelemetryOsVersionMinorBucket;
    networkTypeBucket?: AdminTelemetryNetworkTypeBucket;
    physicalDeviceBucket?: AdminTelemetryPhysicalDeviceBucket;
  },
  locale: AdminDashboardLocale,
): string {
  const parts = [
    compactLabel(
      input.appBuildNumber
        ? locale === "ko"
          ? `빌드 ${input.appBuildNumber.trim()}`
          : `build ${input.appBuildNumber.trim()}`
        : "",
    ),
    compactLabel(formatAdminTelemetryDeviceModelNameBucketLabel(input.deviceModelNameBucket, locale)),
    compactLabel(formatAdminTelemetryOsVersionMinorBucketLabel(input.osVersionMinorBucket, locale)),
    compactLabel(formatAdminTelemetryNetworkTypeBucketLabel(input.networkTypeBucket, locale)),
    compactLabel(formatAdminTelemetryPhysicalDeviceBucketLabel(input.physicalDeviceBucket, locale)),
  ].filter((value): value is string => value != null);

  return parts.length > 0 ? parts.join(" / ") : "-";
}

export function formatAdminTelemetryRuntimeCoverageLabel(
  input: {
    deviceInstallId?: string | null;
    platform?: string | null;
    appBuildNumber?: string;
    deviceModelNameBucket?: AdminTelemetryDeviceModelNameBucket;
    osVersionMinorBucket?: AdminTelemetryOsVersionMinorBucket;
    networkTypeBucket?: AdminTelemetryNetworkTypeBucket;
    physicalDeviceBucket?: AdminTelemetryPhysicalDeviceBucket;
  },
  locale: AdminDashboardLocale,
): string | null {
  const hasDeviceAnchor = compactLabel(input.deviceInstallId ?? "") != null || compactLabel(input.platform ?? "") != null;
  if (!hasDeviceAnchor) {
    return null;
  }

  const visibleRuntimePartCount = [
    compactLabel(input.appBuildNumber ?? ""),
    compactLabel(formatAdminTelemetryDeviceModelNameBucketLabel(input.deviceModelNameBucket, locale)),
    compactLabel(formatAdminTelemetryOsVersionMinorBucketLabel(input.osVersionMinorBucket, locale)),
    compactLabel(formatAdminTelemetryNetworkTypeBucketLabel(input.networkTypeBucket, locale)),
    compactLabel(formatAdminTelemetryPhysicalDeviceBucketLabel(input.physicalDeviceBucket, locale)),
  ].filter((value): value is string => value != null).length;

  if (visibleRuntimePartCount === 0) {
    return text(locale, "Summary source missing runtime snapshot", "요약 source 런타임 스냅샷 누락");
  }
  if (visibleRuntimePartCount < 5) {
    return text(locale, "Summary source has partial runtime snapshot", "요약 source 런타임 스냅샷 일부만 도착");
  }
  return null;
}

export function formatAdminTelemetryReauthMethodBucketLabel(
  value: AdminTelemetryReauthMethodBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "firebase_id_token":
      return text(locale, "Firebase ID token", "Firebase ID 토큰");
    case "oauth_authorization_code":
      return text(locale, "OAuth authorization code", "OAuth authorization code");
    case "oauth_access_token":
      return text(locale, "OAuth access token", "OAuth access token");
    case "unknown":
      return text(locale, "Unknown reauth method", "알 수 없는 재인증 방식");
    default:
      return "-";
  }
}

export function formatAdminTelemetryRecentAuthAgeBucketLabel(
  value: AdminTelemetryRecentAuthAgeBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "lt_5m":
      return text(locale, "Under 5 minutes", "5분 미만");
    case "5m_to_30m":
      return text(locale, "5 to 30 minutes", "5~30분");
    case "30m_to_24h":
      return text(locale, "30 minutes to 24 hours", "30분~24시간");
    case "24h_plus":
      return text(locale, "24 hours or more", "24시간 이상");
    case "unknown":
      return text(locale, "Unknown auth age", "알 수 없는 인증 시각");
    default:
      return "-";
  }
}

export function formatAdminPetSpeciesBucketLabel(
  value: AdminPetSpeciesBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "dog":
      return text(locale, "Dog", "강아지");
    case "cat":
      return text(locale, "Cat", "고양이");
    case "other":
      return text(locale, "Other species", "기타 종");
    case "unset":
      return text(locale, "Species not selected", "종 미선택");
    case "unknown":
      return text(locale, "Unknown species", "알 수 없는 종");
    default:
      return "-";
  }
}

export function formatAdminPetBreedSelectionBucketLabel(
  value: AdminPetBreedSelectionBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "species_unset":
      return text(locale, "Species not selected", "종 미선택");
    case "breed_unset":
      return text(locale, "Breed not entered", "품종 미입력");
    case "catalog_match":
      return text(locale, "Catalog breed", "카탈로그 품종");
    case "free_text":
      return text(locale, "Free-text breed", "자유 입력 품종");
    case "unknown":
      return text(locale, "Unknown breed source", "알 수 없는 품종 입력");
    default:
      return "-";
  }
}

export function formatAdminPetProfileCompletionStepLabel(
  value: AdminPetProfileCompletionStep | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "first_pet_name_only":
      return text(locale, "First pet name only", "첫 펫 이름만 입력");
    case "first_pet_profile_details":
      return text(locale, "First pet profile details", "첫 펫 상세 프로필 입력");
    case "photo_step":
      return text(locale, "Profile photo step", "프로필 사진 단계");
    case "profile_edit":
      return text(locale, "Profile edit", "프로필 수정");
    case "suggestion_apply":
      return text(locale, "Suggestion apply", "제안 반영");
    case "unknown":
      return text(locale, "Unknown profile step", "알 수 없는 프로필 단계");
    default:
      return "-";
  }
}

export function formatAdminPetAttemptCountBucketLabel(
  value: AdminPetAttemptCountBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "0":
      return text(locale, "First attempt", "첫 시도");
    case "1":
      return text(locale, "1 retry", "1회 재시도");
    case "2_to_3":
      return text(locale, "2 to 3 retries", "2~3회 재시도");
    case "4_plus":
      return text(locale, "4 or more retries", "4회 이상 재시도");
    case "unknown":
      return text(locale, "Unknown retry count", "알 수 없는 재시도 횟수");
    default:
      return "-";
  }
}

export function formatAdminPetPhotoConfirmLatencyBucketLabel(
  value: AdminPetPhotoConfirmLatencyBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "lt_5s":
      return text(locale, "Under 5 seconds", "5초 미만");
    case "5s_to_30s":
      return text(locale, "5 to 30 seconds", "5~30초");
    case "30s_to_2m":
      return text(locale, "30 seconds to 2 minutes", "30초~2분");
    case "2m_plus":
      return text(locale, "2 minutes or more", "2분 이상");
    case "unknown":
      return text(locale, "Unknown photo confirm latency", "알 수 없는 사진 확인 지연");
    default:
      return "-";
  }
}

export function formatAdminBooleanLabel(value: boolean | undefined, locale: AdminDashboardLocale): string {
  if (value === true) return text(locale, "Yes", "예");
  if (value === false) return text(locale, "No", "아니오");
  return "-";
}

export function formatAdminTelemetryDraftSourceBucketLabel(
  value: AdminTelemetryDraftSourceBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "manual_text":
      return text(locale, "Manual text", "직접 입력");
    case "speech_capture":
      return text(locale, "Speech capture", "음성 입력");
    case "mixed":
      return text(locale, "Mixed", "혼합");
    case "unknown":
      return text(locale, "Unknown source", "알 수 없는 입력 경로");
    default:
      return "-";
  }
}

export function formatAdminTelemetryMediaAttachCountBucketLabel(
  value: AdminTelemetryMediaAttachCountBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "0":
      return text(locale, "0", "0");
    case "1":
      return text(locale, "1", "1");
    case "2_to_3":
      return text(locale, "2 to 3", "2~3");
    case "4_plus":
      return text(locale, "4+", "4+");
    case "unknown":
      return text(locale, "Unknown count", "알 수 없는 첨부 수");
    default:
      return "-";
  }
}

export function formatAdminSocialTargetUserLookupKindLabel(
  value: AdminSocialTargetUserLookupKind | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "self":
      return text(locale, "Self profile", "내 공개 프로필");
    case "path_user_id":
      return text(locale, "Route target user", "route 대상 사용자");
    case "body_target_user_id":
      return text(locale, "Body target user", "body 대상 사용자");
    case "query_target_user_id":
      return text(locale, "Query target user", "query 대상 사용자");
    case "unknown":
      return text(locale, "Unknown lookup target", "알 수 없는 조회 대상");
    default:
      return "-";
  }
}

export function formatAdminSocialPublicPetCountBucketLabel(
  value: AdminSocialPublicPetCountBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "0":
      return text(locale, "0 public pets", "공개 반려동물 0마리");
    case "1":
      return text(locale, "1 public pet", "공개 반려동물 1마리");
    case "2_to_3":
      return text(locale, "2 to 3 public pets", "공개 반려동물 2~3마리");
    case "4_plus":
      return text(locale, "4+ public pets", "공개 반려동물 4마리 이상");
    case "unknown":
      return text(locale, "Unknown public pet count", "알 수 없는 공개 반려동물 수");
    default:
      return "-";
  }
}

export function formatAdminSocialProfileMutationFieldSetBucketLabel(
  value: AdminSocialProfileMutationFieldSetBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "display_name_only":
      return text(locale, "Display name only", "표시 이름만");
    case "bio_only":
      return text(locale, "Bio only", "소개만");
    case "public_pets_only":
      return text(locale, "Public pets only", "공개 반려동물만");
    case "display_name_and_bio":
      return text(locale, "Display name + bio", "표시 이름 + 소개");
    case "display_name_and_public_pets":
      return text(locale, "Display name + public pets", "표시 이름 + 공개 반려동물");
    case "bio_and_public_pets":
      return text(locale, "Bio + public pets", "소개 + 공개 반려동물");
    case "display_name_bio_and_public_pets":
      return text(locale, "Display name + bio + public pets", "표시 이름 + 소개 + 공개 반려동물");
    case "unknown":
      return text(locale, "Unknown profile mutation", "알 수 없는 프로필 수정");
    default:
      return "-";
  }
}

export function formatAdminChatRoomKindLabel(
  value: AdminChatRoomKind | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "direct":
      return text(locale, "Direct room", "1:1 대화방");
    case "group":
      return text(locale, "Group room", "그룹 대화방");
    case "unknown":
      return text(locale, "Unknown room kind", "알 수 없는 대화방 유형");
    default:
      return "-";
  }
}

export function formatAdminChatMessageLengthBucketLabel(
  value: AdminChatMessageLengthBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "1_to_20":
      return text(locale, "1 to 20 chars", "1~20자");
    case "21_to_100":
      return text(locale, "21 to 100 chars", "21~100자");
    case "101_to_500":
      return text(locale, "101 to 500 chars", "101~500자");
    case "501_to_1000":
      return text(locale, "501 to 1000 chars", "501~1000자");
    case "unknown":
      return text(locale, "Unknown message length", "알 수 없는 메시지 길이");
    default:
      return "-";
  }
}

export function formatAdminChatMembershipCountBucketLabel(
  value: AdminChatMembershipCountBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "1":
      return text(locale, "1 member", "구성원 1명");
    case "2_to_5":
      return text(locale, "2 to 5 members", "구성원 2~5명");
    case "6_plus":
      return text(locale, "6+ members", "구성원 6명 이상");
    case "unknown":
      return text(locale, "Unknown membership count", "알 수 없는 구성원 수");
    default:
      return "-";
  }
}

export function formatAdminMedicationScheduleRecurrenceBucketLabel(
  value: AdminMedicationScheduleRecurrenceBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "daily":
      return text(locale, "Daily schedule", "매일 일정");
    case "weekly":
      return text(locale, "Weekly schedule", "주간 일정");
    case "unknown":
      return text(locale, "Unknown recurrence", "알 수 없는 반복 주기");
    default:
      return "-";
  }
}

export function formatAdminMedicationDosePresenceLabel(
  value: AdminMedicationDosePresence | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "present":
      return text(locale, "Dose entered", "용량 입력");
    case "absent":
      return text(locale, "Dose omitted", "용량 미입력");
    case "unknown":
      return text(locale, "Unknown dose state", "알 수 없는 용량 상태");
    default:
      return "-";
  }
}

export function formatAdminMedicationOccurrenceActionKindLabel(
  value: AdminMedicationOccurrenceActionKind | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "complete":
      return text(locale, "Complete occurrence", "복약 완료");
    case "skip":
      return text(locale, "Skip occurrence", "복약 건너뜀");
    case "snooze":
      return text(locale, "Snooze occurrence", "복약 미루기");
    case "unknown":
      return text(locale, "Unknown occurrence action", "알 수 없는 복약 액션");
    default:
      return "-";
  }
}

export function formatAdminMedicationSnoozeWindowBucketLabel(
  value: AdminMedicationSnoozeWindowBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "lt_30m":
      return text(locale, "Under 30 minutes", "30분 미만");
    case "30m_to_2h":
      return text(locale, "30 minutes to 2 hours", "30분~2시간");
    case "2h_plus":
      return text(locale, "2 hours or more", "2시간 이상");
    case "unknown":
      return text(locale, "Unknown snooze window", "알 수 없는 미루기 구간");
    default:
      return "-";
  }
}

export function formatAdminCommunityUploadSourceBucketLabel(
  value: AdminCommunityUploadSourceBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "native_picker_uri":
      return text(locale, "Native picker asset", "네이티브 선택 파일");
    case "browser_file":
      return text(locale, "Browser file", "브라우저 파일");
    case "metadata_only":
      return text(locale, "Metadata-only fallback", "메타데이터 전용 fallback");
    case "unknown":
      return text(locale, "Unknown upload source", "알 수 없는 업로드 경로");
    default:
      return "-";
  }
}

export function formatAdminCommunityContentLengthBucketLabel(
  value: AdminCommunityContentLengthBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "1_to_80":
      return text(locale, "1 to 80 chars", "1~80자");
    case "81_to_240":
      return text(locale, "81 to 240 chars", "81~240자");
    case "241_to_1000":
      return text(locale, "241 to 1000 chars", "241~1000자");
    case "1001_to_2000":
      return text(locale, "1001 to 2000 chars", "1001~2000자");
    case "unknown":
      return text(locale, "Unknown length", "알 수 없는 길이");
    default:
      return "-";
  }
}

export function formatAdminCommunityImageCountBucketLabel(
  value: AdminCommunityImageCountBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "0":
      return text(locale, "0 images", "이미지 0장");
    case "1":
      return text(locale, "1 image", "이미지 1장");
    case "2_to_3":
      return text(locale, "2 to 3 images", "이미지 2~3장");
    case "4_plus":
      return text(locale, "4+ images", "이미지 4장 이상");
    case "unknown":
      return text(locale, "Unknown image count", "알 수 없는 이미지 수");
    default:
      return "-";
  }
}

export function formatAdminCommunityTopicTagLabel(
  value: AdminCommunityTopicTag | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "daily-life":
      return text(locale, "Daily life", "일상");
    case "health-tip":
      return text(locale, "Health tip", "건강팁");
    case "question":
      return text(locale, "Question", "질문");
    case "food":
      return text(locale, "Food", "식사");
    case "behavior":
      return text(locale, "Behavior", "행동");
    case "recommendation":
      return text(locale, "Recommendation", "추천");
    case "none":
      return text(locale, "No topic tag", "태그 없음");
    case "unknown":
      return text(locale, "Unknown topic", "알 수 없는 태그");
    default:
      return "-";
  }
}

export function formatAdminReceiptExtractSourceBucketLabel(
  value: AdminReceiptExtractSourceBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "native_picker_uri":
      return text(locale, "Native picker URI", "네이티브 picker URI");
    case "browser_file":
      return text(locale, "Browser file", "브라우저 파일");
    case "metadata_only":
      return text(locale, "Metadata only", "메타데이터만");
    case "unknown":
      return text(locale, "Unknown extract source", "알 수 없는 추출 소스");
    default:
      return "-";
  }
}

export function formatAdminReceiptImageByteSizeBucketLabel(
  value: AdminReceiptImageByteSizeBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "lt_1mb":
      return text(locale, "< 1 MB", "1MB 미만");
    case "1mb_to_5mb":
      return text(locale, "1 to 5 MB", "1~5MB");
    case "5mb_to_10mb":
      return text(locale, "5 to 10 MB", "5~10MB");
    case "10mb_plus":
      return text(locale, "10 MB+", "10MB 이상");
    case "unknown":
      return text(locale, "Unknown image size", "알 수 없는 이미지 크기");
    default:
      return "-";
  }
}

export function formatAdminReceiptOcrConfidenceBucketLabel(
  value: AdminReceiptOcrConfidenceBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "lt_50":
      return text(locale, "Below 50%", "50% 미만");
    case "50_to_79":
      return text(locale, "50 to 79%", "50~79%");
    case "80_to_94":
      return text(locale, "80 to 94%", "80~94%");
    case "95_plus":
      return text(locale, "95%+", "95% 이상");
    case "unknown":
      return text(locale, "Unknown OCR confidence", "알 수 없는 OCR 확신도");
    default:
      return "-";
  }
}

export function formatAdminReceiptCategoryMutationTypeLabel(
  value: AdminReceiptCategoryMutationType | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "custom_create":
      return text(locale, "Custom category create", "커스텀 카테고리 생성");
    case "custom_update":
      return text(locale, "Custom category update", "커스텀 카테고리 수정");
    case "custom_delete":
      return text(locale, "Custom category delete", "커스텀 카테고리 삭제");
    case "unknown":
      return text(locale, "Unknown category mutation", "알 수 없는 카테고리 변경");
    default:
      return "-";
  }
}

export function formatAdminHealthFileSizeBucketLabel(
  value: AdminHealthFileSizeBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "lt_1mb":
      return text(locale, "< 1 MB", "1MB 미만");
    case "1mb_to_5mb":
      return text(locale, "1 to 5 MB", "1~5MB");
    case "5mb_to_20mb":
      return text(locale, "5 to 20 MB", "5~20MB");
    case "20mb_plus":
      return text(locale, "20 MB+", "20MB 이상");
    case "unknown":
      return text(locale, "Unknown file size", "알 수 없는 파일 크기");
    default:
      return "-";
  }
}

export function formatAdminHealthFileTypeBucketLabel(
  value: AdminHealthFileTypeBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "pdf":
      return text(locale, "PDF document", "PDF 문서");
    case "image":
      return text(locale, "Image scan", "이미지 스캔");
    case "other":
      return text(locale, "Other file type", "기타 파일 형식");
    case "unknown":
      return text(locale, "Unknown file type", "알 수 없는 파일 형식");
    default:
      return "-";
  }
}

export function formatAdminHealthAnalysisFallbackPathLabel(
  value: AdminHealthAnalysisFallbackPath | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "none":
      return text(locale, "No fallback", "Fallback 없음");
    case "manual_timeout":
      return text(locale, "Manual timeout fallback", "시간 초과 수동 fallback");
    case "unknown":
      return text(locale, "Unknown fallback path", "알 수 없는 fallback 경로");
    default:
      return "-";
  }
}

export function formatAdminHealthConfirmToSaveLatencyBucketLabel(
  value: AdminHealthConfirmToSaveLatencyBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "lt_10s":
      return text(locale, "Under 10 seconds", "10초 미만");
    case "10s_to_60s":
      return text(locale, "10 to 60 seconds", "10~60초");
    case "1m_to_5m":
      return text(locale, "1 to 5 minutes", "1~5분");
    case "5m_plus":
      return text(locale, "5 minutes+", "5분 이상");
    case "unknown":
      return text(locale, "Unknown save latency", "알 수 없는 저장 지연");
    default:
      return "-";
  }
}

export function formatAdminRideVisitLeadTimeBucketLabel(
  value: AdminRideVisitLeadTimeBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "past_due":
      return text(locale, "Past due visit", "이미 지난 방문");
    case "lt_2h":
      return text(locale, "Under 2 hours", "2시간 미만");
    case "2h_to_24h":
      return text(locale, "2 hours to 24 hours", "2시간~24시간");
    case "1d_to_3d":
      return text(locale, "1 to 3 days", "1~3일");
    case "3d_plus":
      return text(locale, "3 days+", "3일 이상");
    case "unknown":
      return text(locale, "Unknown visit lead time", "알 수 없는 방문 리드타임");
    default:
      return "-";
  }
}

export function formatAdminRideLongestAddressLengthBucketLabel(
  value: AdminRideLongestAddressLengthBucket | undefined,
  locale: AdminDashboardLocale,
): string {
  switch (value) {
    case "1_to_40":
      return text(locale, "1 to 40 chars", "1~40자");
    case "41_to_120":
      return text(locale, "41 to 120 chars", "41~120자");
    case "121_to_200":
      return text(locale, "121 to 200 chars", "121~200자");
    case "unknown":
      return text(locale, "Unknown address length", "알 수 없는 주소 길이");
    default:
      return "-";
  }
}
